/**
 * How a filter reads the row's value and its own value, per field type.
 *
 * The same filter means different things on different columns. `equal` on a
 * text column is a lowercased string comparison; on a number column it is a
 * numeric one, so that "1.0" and "1" match; on a duration column the filter
 * value has to be parsed through the field's format first, because "3" means
 * three minutes under `h:mm` and three hours under `d h`.
 *
 * Upstream models this as a small class hierarchy hanging off the filter type.
 * Here it is a lookup from field to handler, which is the same dispatch without
 * the inheritance -- and being data, it can be read to see what a filter will do
 * to a column without running it.
 */
import type { Field } from '@/api/types'

import {
  durationFormatOf,
  parseDurationValue,
  roundDurationValueToFormat,
} from '../fields/duration'

/** A parsed value, ready to compare against another of the same kind. */
/**
 * A parsed value, ready to compare against another of the same kind.
 *
 * `null` is a real parsed value, not an absence: a duration handler produces it
 * for a blank, and two blanks comparing equal is what makes an `equal` filter
 * with a cleared value match the empty cells on a duration column.
 */
export type ParsedFilterValue = string | number | null

export interface FilterValueHandler {
  /** Reads the value stored on the row. */
  parseRowValue: (value: unknown, field: Field) => ParsedFilterValue
  /** Reads the value typed into the filter. */
  parseFilterValue: (value: unknown, field: Field) => ParsedFilterValue
}

/**
 * Stringifies exactly as `.toString()` does, without throwing on an absence.
 *
 * Upstream calls `.toString()` on the value directly, which reproduces these
 * cases but raises a TypeError on `undefined` -- and a throw here aborts the
 * whole row-match pass rather than one filter. An array joins on commas and a
 * plain object becomes "[object Object]", both matching what upstream produces,
 * because a filter value can be a substring of either.
 */
export function stringify(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return String(value)
  }
  if (Array.isArray(value)) return value.map(stringify).join(',')
  return '[object Object]'
}

/** Lowercased and trimmed, treating an absence as the empty string. */
function asText(value: unknown): string {
  return stringify(value).toLowerCase().trim()
}

/** Text and everything that has no numeric reading. */
export const textHandler: FilterValueHandler = {
  parseRowValue: (value) => asText(value),
  parseFilterValue: (value) => asText(value),
}

/**
 * Numbers, ratings and autonumbers.
 *
 * An empty value parses to `NaN`, which compares false against everything --
 * including itself. That matters: it is why an `equal` filter with a cleared
 * value behaves differently on a number column than on a text one, where the
 * empty string is caught explicitly and the filter abstains.
 */
function parseNumeric(value: unknown): number {
  if (value === '' || value === null) return NaN
  return Number(asText(value))
}

export const numberHandler: FilterValueHandler = {
  parseRowValue: (value) => parseNumeric(value),
  parseFilterValue: (value) => parseNumeric(value),
}

/** Ratings parse exactly as numbers do; the difference is the input shown. */
export const ratingHandler: FilterValueHandler = numberHandler

/**
 * Durations, whose filter value is read through the field's own format.
 *
 * A duration is stored as a number of seconds, but the filter is typed in the
 * format the column displays -- so "3" is three minutes under `h:mm` and three
 * hours under `d h`. Parsing then rounding to the format is what makes a filter
 * for "1:30" match a cell that stores 5400 rather than 5400.4.
 */
export const durationHandler: FilterValueHandler = {
  parseRowValue: (value, field) => {
    // Already in seconds, as the API sends it.
    if (Number.isInteger(value)) return value as number
    return parseDuration(value, field)
  },
  parseFilterValue: (value, field) => {
    // A blank parses to null, not NaN -- and two nulls compare equal, which is
    // what makes an `equal` filter with a cleared value match the cells a
    // duration column cannot read either.
    if (stringify(value).trim() === '') return null
    const asNumber = Number(value)
    return parseDuration(Number.isFinite(asNumber) ? asNumber : value, field)
  },
}

function parseDuration(value: unknown, field: Field): number | null {
  const format = durationFormatOf(field)
  const parsed = parseDurationValue(value as string | number | null, format)
  if (parsed === null) return null
  return roundDurationValueToFormat(parsed, format)
}
